import React from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Image,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import { FiUser, FiLock } from 'react-icons/fi';
import LanguageSelector from '@/components/common/LanguageSelector';

interface LoginFormData {
  email: string;
  password: string;
}

const Login: React.FC = () => {
  const { t } = useTranslation();
  const { isAuthenticated, login, isLoading, error, clearError } = useAuthStore();
  const location = useLocation();
  
  // Get the intended destination from location state, default to dashboard
  const from = (location.state as { from?: Location })?.from?.pathname || '/';
  
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>();
  
  const onSubmit = async (data: LoginFormData) => {
    await login(data.email, data.password);
  };
  
  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }
  
  return (
    <Flex justify="center">
      <Box maxW="sm" w="full" mx={4} py={[4, 10]} h="100vh" display="flex" flexDir="column" justifyContent="space-between">
        <Box />
        <Box>
          <Flex justify="flex-end" mb={6}>
            <LanguageSelector />
          </Flex>

          <Flex mb={12} flexDir="column" align="center" justify="center" gap={3}>
            <Image alt="letsparky" src="/letsparky-logo.png" w={40} />
            <Text fontSize="sm" color="gray.400">{t('auth.smartParkingManagement')}</Text>
          </Flex>

          <Box mb={6} display="inline-block" borderBottom="2px" borderColor="rgb(248 171 22)">
            <Text pb={1} color="rgb(248 171 22)">{t('auth.login')}</Text>
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={!!errors.email} mb={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color="blue.700">
                  <FiUser size={14} />
                </InputLeftElement>
                <Input
                  id="email"
                  placeholder={t('auth.email')}
                  autoComplete="email"
                  {...register('email', { required: t('auth.emailRequired') })}
                />
              </InputGroup>
              {errors.email && <FormErrorMessage>{errors.email.message}</FormErrorMessage>}
            </FormControl>
            
            <FormControl isInvalid={!!errors.password} mb={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color="blue.700">
                  <FiLock size={14} />
                </InputLeftElement>
                <Input
                  id="password"
                  type="password"
                  placeholder={t('auth.password')}
                  autoComplete="current-password"
                  {...register('password', { required: t('auth.passwordRequired') })}
                />
              </InputGroup>
              {errors.password && <FormErrorMessage>{errors.password.message}</FormErrorMessage>}
            </FormControl>

            {error && (
              <Alert status="error" mb={4}>
                <AlertIcon />
                {error}
              </Alert>
            )}

            <Flex mt={8} justify="flex-end">
              <Button 
                type="submit" 
                bg="#ff8c00"
                color="white"
                _hover={{ bg: "#ffbb77" }}
                px={8} 
                isLoading={isLoading}
              >
                {t('auth.login')}
              </Button>
            </Flex>
          </form>
        </Box>

        <Flex flexDir="column" align="center" gap={4}>
          <Flex flexDir="column" gap={2}>
            <Text fontSize="xs" color="gray.400" textAlign="center">{t('auth.copyright')}</Text>
            <Text fontSize="xs" color="gray.400" textAlign="center" whiteSpace="nowrap">
              {t('auth.allRightsReserved')}
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
};

export default Login; 